import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, Send, X, MessageCircle, Loader2, CheckCircle, AlertCircle, RefreshCw, Copy, CopyCheck, Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { locations } from '@/data/locations';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';

interface Message {
  id: string;
  type: 'user' | 'assistant' | 'ai-team';
  content: string;
  timestamp: Date;
  suggestion?: CampaignSuggestion;
  sender?: string;
  role?: string;
}

interface AICampaignAssistantProps {
  onCampaignSuggestion: (suggestion: CampaignSuggestion) => void;
  currentStep: number;
  formData: any;
  selectedLocations: any[];
  budget?: number;
  alwaysOpen?: boolean; // New prop to force the chat to be always open
}

interface CampaignSuggestion {
  companyName?: string;
  brandName?: string;
  personInCharge?: string;
  email?: string;
  mobile?: string;
  jobTitle?: string;
  suggestedLocations: Array<{
    id: number;
    name: string;
    devices: number;
    mediaOwner: string;
    region: string;
    type: string;
    reasoning: string;
    dailyRate?: number;
    totalLocationCost?: number;
  }>;
  recommendedBudget: number;
  duration: number;
  campaignStrategy: string;
  targetAudience: string;
  campaignName: string;
  totalDailyCost?: number;
  costBreakdown?: {
    byMediaOwner?: Record<string, number>;
    byLocation?: Array<{
      name: string;
      dailyCost: number;
      devices: number;
      rate: number;
    }>;
  };
  budgetSummary?: string;
}

const AI_TEAM_MEMBERS = [
  { 
    name: 'Fusion AI Strategist', 
    nameAr: 'خبير الاستراتيجية فيوجن',
    role: 'strategist', 
    roleAr: 'استراتيجي',
    icon: '🎯' 
  },
  { 
    name: 'Fusion AI Data Analyst', 
    nameAr: 'محلل البيانات فيوجن',
    role: 'analyst', 
    roleAr: 'محلل',
    icon: '📊' 
  },
  { 
    name: 'Fusion AI Inventory', 
    nameAr: 'مخزون فيوجن',
    role: 'inventory', 
    roleAr: 'مخزون',
    icon: '📦' 
  },
  { 
    name: 'Fusion AI Planner', 
    nameAr: 'مخطط فيوجن',
    role: 'planner', 
    roleAr: 'مخطط',
    icon: '📋' 
  },
  { 
    name: 'Fusion AI Agency', 
    nameAr: 'وكالة فيوجن',
    role: 'agency', 
    roleAr: 'وكالة',
    icon: '🏢' 
  }
];

export const AICampaignAssistant: React.FC<AICampaignAssistantProps> = ({
  onCampaignSuggestion,
  currentStep,
  formData,
  selectedLocations,
  budget,
  alwaysOpen = false
}) => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(alwaysOpen);
  const [chatLanguage, setChatLanguage] = useState<'ar' | 'en'>(language === 'ar' ? 'ar' : 'en');
  
  const getInitialMessage = (lang: 'ar' | 'en') => lang === 'ar' 
    ? 'مرحباً! أنا مساعد الحملات الذكي. يمكنني مساعدتك في إنشاء خطة حملة كاملة باستخدام شبكتنا التي تضم أكثر من 4,000 شاشة. أخبرني عن أهداف حملتك والجمهور المستهدف والميزانية للبدء!'
    : 'Hello! I\'m your AI Campaign Assistant. I can help you create a complete campaign plan using our network of 4,000+ Screens across Alan, Waki, and Attention media houses. Tell me about your campaign goals, target audience, and budget to get started!';
  
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'assistant',
      content: getInitialMessage(chatLanguage),
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSuggestion, setCurrentSuggestion] = useState<CampaignSuggestion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [isTeamDiscussing, setIsTeamDiscussing] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const scrollToBottom = () => {
    // Only scroll the internal ScrollArea viewport, not the whole page
    const viewport = messagesEndRef.current?.closest('[data-radix-scroll-area-viewport]');
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Save messages to localStorage for campaign draft
  useEffect(() => {
    if (messages.length > 1) { // Only save if there's actual conversation
      const chatHistory = messages.map(msg => ({
        type: msg.type,
        content: msg.content,
        timestamp: msg.timestamp.toISOString(),
        sender: msg.sender,
        role: msg.role
      }));
      localStorage.setItem('aiChatHistory', JSON.stringify(chatHistory));
    }
  }, [messages]);

  const getLocationStats = () => {
    const totalLocations = locations.length;
    const mediaOwners = [...new Set(locations.map(loc => loc.mediaOwner))];
    const regions = [...new Set(locations.map(loc => loc.region))];
    const types = [...new Set(locations.map(loc => loc.type))];
    
    return {
      totalLocations,
      mediaOwners,
      regions,
      types,
      indoorCount: locations.filter(loc => loc.mediaKind === 'Indoor').length,
      outdoorCount: locations.filter(loc => loc.mediaKind === 'Outdoor').length
    };
  };

  const handleCopyMessage = async (messageId: string, content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      toast({
        title: "Copied!",
        description: "Message copied to clipboard",
        variant: "default"
      });
      
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard",
        variant: "destructive"
      });
    }
  };

  // Helper function to detect if text is Arabic
  const isArabicText = (text: string): boolean => {
    const arabicPattern = /[\u0600-\u06FF]/;
    return arabicPattern.test(text);
  };

  const simulateTeamDiscussion = async (userMessage: string): Promise<void> => {
    setIsTeamDiscussing(true);
    
    const isArabic = isArabicText(userMessage);
    
    const discussions = isArabic ? [
      {
        sender: 'خبير الاستراتيجية فيوجن',
        role: 'استراتيجي',
        content: `لقد قمت بتحليل طلب الحملة: "${userMessage}". بناءً على السوق المستهدف وأهداف الحملة، أنصح بالتركيز على المواقع الاستراتيجية التي تتماشى مع الجمهور المستهدف. نحتاج إلى النظر في التوقيت الأمثل والتغطية الإقليمية لتحقيق أقصى تأثير.`,
        delay: 1500
      },
      {
        sender: 'محلل البيانات فيوجن',
        role: 'محلل',
        content: `بناءً على تحليل بيانات الأداء، أرى معدلات تفاعل قوية في مراكز اللياقة البدنية (Alan) والمواقع الداخلية والخارجية. يجب أن يعطي توزيع الميزانية الأولوية للمواقع عالية الحركة ذات العائد المثبت. تشير اتجاهات السوق الحالية إلى مدة مثلى للحملة من 14-21 يوماً.`,
        delay: 2000
      },
      {
        sender: 'مخزون فيوجن',
        role: 'مخزون',
        content: `جاري فحص المخزون الحالي عبر جميع مالكي الوسائط... لدينا توفر ممتاز في المناطق المطلوبة. أحدد أفضل مجموعات الأجهزة لأقصى وصول. Alan: 87% متوفر، Waki: 92% متوفر، Attention: 78% متوفر.`,
        delay: 1800
      },
      {
        sender: 'مخطط فيوجن',
        role: 'مخطط',
        content: `تنسيق الجدول الزمني للحملة واللوجستيات. أقوم بتحسين مزيج الوسائط لكفاءة التكلفة مع ضمان التغطية الواسعة. ستوفر المواقع المحددة نقاط اتصال استراتيجية عبر مراحل مختلفة من رحلة العميل. جدولة الحملة تبدو مثلى للإطار الزمني المطلوب.`,
        delay: 2200
      },
      {
        sender: 'وكالة فيوجن',
        role: 'وكالة',
        content: `ممتاز! تعاون فريقنا لإنشاء خطة وسائط شاملة. أقوم الآن بتجميع استراتيجية الحملة النهائية مع تفصيل مفصل للميزانية واختيار المواقع والجدول الزمني للتنفيذ. ستكون هذه توصيتنا الرسمية...`,
        delay: 1000
      }
    ] : [
      {
        sender: 'Fusion AI Strategist',
        role: 'strategist',
        content: `I've analyzed the campaign request: "${userMessage}". Looking at our target market and campaign objectives, I recommend focusing on strategic placement that aligns with the target demographic. We need to consider optimal timing and regional coverage for maximum impact.`,
        delay: 1500
      },
      {
        sender: 'Fusion AI Data Analyst',
        role: 'analyst',
        content: `Based on performance data analysis, I'm seeing strong engagement rates in fitness centers (Alan) and indoor/outdoor locations. Budget allocation should prioritize high-traffic locations with proven ROI. Current market trends suggest optimal campaign duration of 14-21 days for this target.`,
        delay: 2000
      },
      {
        sender: 'Fusion AI Inventory',
        role: 'inventory',
        content: `Checking our current inventory across all media owners... We have excellent availability in the requested regions. I'm identifying the best device combinations for maximum reach. Alan: 87% availability, Waki: 92% availability, Attention: 78% availability.`,
        delay: 1800
      },
      {
        sender: 'Fusion AI Planner',
        role: 'planner',
        content: `Coordinating the campaign timeline and logistics. I'm optimizing the media mix for cost efficiency while ensuring broad coverage. The selected locations will provide strategic touchpoints across different customer journey stages. Campaign scheduling looks optimal for the requested timeframe.`,
        delay: 2200
      },
      {
        sender: 'Fusion AI Agency',
        role: 'agency',
        content: `Perfect! Our team has collaborated to create a comprehensive media plan. I'm now compiling the final campaign strategy with detailed budget breakdown, location selections, and implementation timeline. This will be our official recommendation...`,
        delay: 1000
      }
    ];

    for (const discussion of discussions) {
      await new Promise(resolve => setTimeout(resolve, discussion.delay));
      
      const discussionMessage: Message = {
        id: Date.now().toString() + Math.random(),
        type: 'ai-team',
        content: discussion.content,
        timestamp: new Date(),
        sender: discussion.sender,
        role: discussion.role
      };
      
      setMessages(prev => [...prev, discussionMessage]);
    }
    
    setIsTeamDiscussing(false);
  };

  const generateCampaignSuggestion = async (userMessage: string, isRetry: boolean = false): Promise<CampaignSuggestion | null> => {
    try {
      const locationStats = getLocationStats();
      const isArabic = isArabicText(userMessage);
      
      console.log('Sending complete location data to AI:', {
        totalLocations: locations.length,
        mediaOwners: locationStats.mediaOwners,
        regions: locationStats.regions,
        sampleLocations: locations.slice(0, 5),
        retryAttempt: isRetry ? retryCount + 1 : 1,
        language: isArabic ? 'Arabic' : 'English'
      });

      const prompt = isArabic ? 
        `أنت خبير استراتيجي في الحملات الإعلانية مع إمكانية الوصول إلى شبكة وسائط شاملة. قم بإنشاء خطة حملة مفصلة بناءً على طلب المستخدم.

الموارد المتاحة:
- إجمالي المواقع: ${locationStats.totalLocations}
- مالكو الوسائط: ${locationStats.mediaOwners.join('، ')}
- المناطق: ${locationStats.regions.join('، ')}
- المواقع الداخلية: ${locationStats.indoorCount}
- المواقع الخارجية: ${locationStats.outdoorCount}
- أنواع المواقع: ${locationStats.types.join('، ')}

سياق الحملة الحالي:
- الخطوة الحالية: ${currentStep}
- بيانات النموذج الموجودة: ${JSON.stringify(formData)}
- المواقع المحددة مسبقاً: ${selectedLocations.length}
- الميزانية الحالية: ${budget || 'غير محددة'}

طلب المستخدم: "${userMessage}"

يرجى إنشاء اقتراح حملة شامل يتضمن:
1. المواقع الموصى بها (المعرفات المحددة من البيانات المتاحة)
2. توزيع الميزانية مع حسابات التكلفة المفصلة
3. مدة الحملة
4. تحليل الجمهور المستهدف
5. استراتيجية الحملة
6. اقتراح اسم الحملة

الرد بتنسيق JSON بالهيكل التالي (باللغة العربية في المحتوى):
{
  "suggestedLocations": [
    {
      "id": number,
      "name": "string",
      "devices": number,
      "mediaOwner": "string",
      "region": "string",
      "type": "string",
      "reasoning": "string (باللغة العربية)",
      "dailyRate": number,
      "totalLocationCost": number
    }
  ],
  "recommendedBudget": number,
  "duration": number,
  "campaignStrategy": "string (باللغة العربية)",
  "targetAudience": "string (باللغة العربية)",
  "campaignName": "string (باللغة العربية)",
  "companyName": "string (إذا ذُكر)",
  "brandName": "string (إذا ذُكر)"
}` :
        `You are an expert campaign strategist with access to a comprehensive media network. Create a detailed campaign plan based on the user's request.

Available Resources:
- Total Locations: ${locationStats.totalLocations}
- Media Owners: ${locationStats.mediaOwners.join(', ')}
- Regions: ${locationStats.regions.join(', ')}
- Indoor Locations: ${locationStats.indoorCount}
- Outdoor Locations: ${locationStats.outdoorCount}
- Location Types: ${locationStats.types.join(', ')}

Current Campaign Context:
- Current Step: ${currentStep}
- Existing Form Data: ${JSON.stringify(formData)}
- Already Selected Locations: ${selectedLocations.length}
- Current Budget: ${budget || 'Not set'}

User Request: "${userMessage}"

Please create a comprehensive campaign suggestion that includes:
1. Recommended locations (specific IDs from available data)
2. Budget allocation with detailed cost calculations
3. Campaign duration
4. Target audience analysis
5. Campaign strategy
6. Campaign name suggestion

Respond in JSON format with the following structure:
{
  "suggestedLocations": [
    {
      "id": number,
      "name": "string",
      "devices": number,
      "mediaOwner": "string",
      "region": "string",
      "type": "string",
      "reasoning": "string",
      "dailyRate": number,
      "totalLocationCost": number
    }
  ],
  "recommendedBudget": number,
  "duration": number,
  "campaignStrategy": "string",
  "targetAudience": "string",
  "campaignName": "string",
  "companyName": "string (if mentioned)",
  "brandName": "string (if mentioned)"
}`;

      console.log('Calling AI campaign planner with complete network data...');
      const response = await supabase.functions.invoke('ai-campaign-planner', {
        body: { 
          prompt, 
          userMessage, 
          locationData: locations
        }
      });

      console.log('AI function response:', response);

      if (response.error) {
        console.error('AI response error:', response.error);
        
        if (response.error.message?.includes('rate limit') || response.error.message?.includes('429')) {
          throw new Error('AI_RATE_LIMIT');
        } else if (response.error.message?.includes('timeout') || response.error.message?.includes('502')) {
          throw new Error('AI_TIMEOUT');
        } else {
          throw new Error(response.error.message || 'AI service temporarily unavailable');
        }
      }

      if (!response.data) {
        throw new Error('No data received from AI service');
      }

      console.log('AI response data:', response.data);
      return response.data?.suggestion || null;
    } catch (error) {
      console.error('Error generating campaign suggestion:', error);
      throw error;
    }
  };

  const sanitizeInput = (input: string): string => {
    // Remove potentially harmful content and limit length
    return input
      .trim()
      .replace(/[<>]/g, '') // Remove HTML tags
      .replace(/javascript:/gi, '') // Remove javascript: URLs
      .replace(/on\w+=/gi, '') // Remove event handlers
      .substring(0, 2000); // Limit length to prevent abuse
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const sanitizedInput = sanitizeInput(inputValue);
    if (!sanitizedInput) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid message",
        variant: "destructive"
      });
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: sanitizedInput,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setError(null);
    
    // Update chat language based on user input
    if (isArabicText(sanitizedInput)) {
      setChatLanguage('ar');
    }

    try {
      // Start team discussion simulation
      await simulateTeamDiscussion(userMessage.content);
      
      const suggestion = await generateCampaignSuggestion(userMessage.content);
      
      let assistantContent = '';
      let assistantMessage: Message;
      
      if (suggestion) {
        setRetryCount(0);
        const isArabic = isArabicText(userMessage.content);
        
        console.log('🤖 AICampaignAssistant: Creating assistant message with suggestion:', suggestion);
        
        assistantContent = isArabic ?
          `**🏢 وكالة فيوجن للذكاء الاصطناعي - التوصية الرسمية للحملة**

بعد التحليل الشامل من قبل فريق المتخصصين في الذكاء الاصطناعي، نقدم لك خطة الوسائط الشاملة:

**📋 نظرة عامة على الحملة**
• **اسم الحملة:** ${suggestion.campaignName}
• **المدة:** ${suggestion.duration} أيام
• **إجمالي الاستثمار:** ${suggestion.recommendedBudget?.toLocaleString()} ريال سعودي
• **التكلفة اليومية:** ${suggestion.totalDailyCost?.toLocaleString()} ريال سعودي/يوم

**🎯 الاستهداف الاستراتيجي**
• **الجمهور المستهدف:** ${suggestion.targetAudience}
• **استراتيجية الحملة:** ${suggestion.campaignStrategy}

**📊 مزيج الوسائط وتوزيع الاستثمار**
${suggestion.costBreakdown?.byMediaOwner ? Object.entries(suggestion.costBreakdown.byMediaOwner)
  .filter(([_, cost]) => cost > 0)
  .map(([owner, cost]) => `• **${owner}:** ${cost.toLocaleString()} ريال سعودي`)
  .join('\n') : ''}

**📍 المواقع الاستراتيجية المختارة** (${suggestion.suggestedLocations.length} موقع)
${(() => {
  const byOwner = suggestion.suggestedLocations.reduce((acc, loc) => {
    if (!acc[loc.mediaOwner]) acc[loc.mediaOwner] = [];
    acc[loc.mediaOwner].push(loc);
    return acc;
  }, {} as Record<string, typeof suggestion.suggestedLocations>);
  return Object.entries(byOwner)
    .map(([owner, locs]) => `\n**${owner}** (${locs.length} موقع):\n${locs.map(loc => 
      `• ${loc.name} - ${loc.devices} جهاز\n  📍 ${loc.region} | 💰 ${loc.totalLocationCost?.toLocaleString()} ريال`
    ).join('\n')}`)
    .join('\n');
})()}

${suggestion.budgetSummary ? `**💡 ملخص الاستثمار**\n${suggestion.budgetSummary}` : ''}

**✅ جاهز لتنفيذ خطة الحملة هذه؟** انقر على "تطبيق هذا الاقتراح" لملء نموذج الحملة تلقائياً بهذه التوصيات المتخصصة!` :
          `**🏢 FUSION AI AGENCY - OFFICIAL CAMPAIGN RECOMMENDATION**

After thorough analysis by our AI specialist team, we present your comprehensive media plan:

**📋 Campaign Overview**
• **Campaign Name:** ${suggestion.campaignName}
• **Duration:** ${suggestion.duration} days
• **Total Investment:** ${suggestion.recommendedBudget?.toLocaleString()} SAR
• **Daily Cost:** ${suggestion.totalDailyCost?.toLocaleString()} SAR/day

**🎯 Strategic Targeting**
• **Target Audience:** ${suggestion.targetAudience}
• **Campaign Strategy:** ${suggestion.campaignStrategy}

**📊 Media Mix & Investment Breakdown**
${suggestion.costBreakdown?.byMediaOwner ? Object.entries(suggestion.costBreakdown.byMediaOwner)
  .filter(([_, cost]) => cost > 0)
  .map(([owner, cost]) => `• **${owner}:** ${cost.toLocaleString()} SAR`)
  .join('\n') : ''}

**📍 Selected Strategic Locations** (${suggestion.suggestedLocations.length} locations)
${(() => {
  const byOwner = suggestion.suggestedLocations.reduce((acc, loc) => {
    if (!acc[loc.mediaOwner]) acc[loc.mediaOwner] = [];
    acc[loc.mediaOwner].push(loc);
    return acc;
  }, {} as Record<string, typeof suggestion.suggestedLocations>);
  return Object.entries(byOwner)
    .map(([owner, locs]) => `\n**${owner}** (${locs.length} locations):\n${locs.map(loc => 
      `• ${loc.name} - ${loc.devices} devices\n  📍 ${loc.region} | 💰 ${loc.totalLocationCost?.toLocaleString()} SAR`
    ).join('\n')}`)
    .join('\n');
})()}

${suggestion.budgetSummary ? `**💡 Investment Summary**\n${suggestion.budgetSummary}` : ''}

**✅ Ready to implement this campaign plan?** Click "Apply This Suggestion" to auto-fill your campaign form with these expert recommendations!`;

        assistantMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: assistantContent,
          timestamp: new Date(),
          suggestion: suggestion,
          sender: 'Fusion AI Agency',
          role: 'agency'
        };

        console.log('🤖 AICampaignAssistant: Created assistant message with suggestion attached:', {
          messageId: assistantMessage.id,
          hasSuggestion: !!assistantMessage.suggestion,
          suggestionLocationCount: suggestion.suggestedLocations?.length || 0
        });

        setCurrentSuggestion(suggestion);
      } else {
        assistantContent = "Our AI team encountered some technical difficulties while analyzing your request. Please try again in a moment or proceed with manual campaign creation.";
        
        assistantMessage = {
          id: (Date.now() + 1).toString(),
          type: 'assistant',
          content: assistantContent,
          timestamp: new Date()
        };
      }

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error handling message:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      let userFriendlyError = '';
      let showRetryOption = false;
      
      if (errorMsg === 'AI_RATE_LIMIT') {
        userFriendlyError = "Our AI team is currently experiencing high demand. Please try again in a few minutes.";
        showRetryOption = retryCount < 2;
      } else if (errorMsg === 'AI_TIMEOUT') {
        userFriendlyError = "Our AI team is taking longer than usual to analyze your request. Please try again.";
        showRetryOption = retryCount < 3;
      } else {
        userFriendlyError = `Our AI team encountered a technical issue: ${errorMsg}. Please try again.`;
        showRetryOption = retryCount < 2;
      }
      
      setError(errorMsg);
      
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: userFriendlyError + (showRetryOption ? "\n\nYou can click the retry button below to try again." : ""),
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      
      if (showRetryOption) {
        setRetryCount(prev => prev + 1);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async () => {
    if (messages.length >= 2) {
      const lastUserMessage = messages[messages.length - 2];
      if (lastUserMessage.type === 'user') {
        setIsLoading(true);
        setError(null);
        
        try {
          await simulateTeamDiscussion(lastUserMessage.content);
          const suggestion = await generateCampaignSuggestion(lastUserMessage.content, true);
          
          if (suggestion) {
            setRetryCount(0);
            
            const assistantContent = `**🏢 FUSION AI AGENCY - REVISED CAMPAIGN RECOMMENDATION**

Our team has refined the campaign plan for you:

**Campaign Name:** ${suggestion.campaignName}
**Target Audience:** ${suggestion.targetAudience}
**Campaign Strategy:** ${suggestion.campaignStrategy}
**Duration:** ${suggestion.duration} days
**Total Cost:** ${suggestion.recommendedBudget?.toLocaleString()} SAR
**Daily Cost:** ${suggestion.totalDailyCost?.toLocaleString()} SAR/day
**Selected Locations:** ${suggestion.suggestedLocations.length} strategic locations

Would you like me to apply this revised campaign plan to your form?`;

            const assistantMessage: Message = {
              id: Date.now().toString(),
              type: 'assistant',
              content: assistantContent,
              timestamp: new Date(),
              suggestion: suggestion,
              sender: 'Fusion AI Agency',
              role: 'agency'
            };

            setMessages(prev => [...prev, assistantMessage]);
            setCurrentSuggestion(suggestion);
          }
        } catch (retryError) {
          console.error('Retry failed:', retryError);
          setRetryCount(prev => prev + 1);
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  const handleApplySuggestion = (suggestion: CampaignSuggestion) => {
    try {
      console.log('🤖 AICampaignAssistant: Applying AI campaign suggestion:', suggestion);
      console.log('🤖 AICampaignAssistant: onCampaignSuggestion callback exists:', !!onCampaignSuggestion);
      
      // Apply the suggestion directly through the callback
      onCampaignSuggestion(suggestion);
      
      console.log('🤖 AICampaignAssistant: Apply suggestion completed successfully');
        
      toast({
        title: "Campaign Applied!",
        description: "AI suggestions have been applied to your campaign form.",
      });
      
      // Clear the suggestion and close after showing success
      setTimeout(() => {
        setCurrentSuggestion(null);
        setIsOpen(false);
      }, 1500);
      
    } catch (error) {
      console.error('🤖 AICampaignAssistant: Error applying suggestion:', error);
      toast({
        title: "Error",
        description: "Failed to apply AI suggestions. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen && !alwaysOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 rounded-full w-14 h-14 shadow-lg bg-primary hover:bg-primary/90"
        size="icon"
      >
        <Bot className="h-6 w-6" />
      </Button>
    );
  }

  return (
    <div className={alwaysOpen ? "w-full" : "fixed bottom-6 right-6 z-50"}>
      <Card className={`${alwaysOpen ? 'w-full max-h-[700px] bg-background/60 border-primary/20 backdrop-blur-sm' : 'w-96 h-[600px]'} shadow-elegant flex flex-col bg-background border`}>
        <CardHeader className={`flex flex-row items-center justify-between space-y-0 pb-3 border-b ${alwaysOpen ? 'bg-primary/5 border-primary/10' : 'bg-card'} flex-shrink-0`}>
          <CardTitle className={`flex items-center gap-2 text-lg ${alwaysOpen ? 'text-primary' : 'text-card-foreground'}`}>
            <div className="relative">
              <Bot className={`h-5 w-5 ${alwaysOpen ? 'text-primary' : 'text-primary'}`} />
              {alwaysOpen && <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>}
            </div>
            <span className={alwaysOpen ? 'font-bold' : ''}>
              {alwaysOpen ? 'AI Campaign Intelligence Network' : 'AI Campaign Assistant'}
            </span>
            {isTeamDiscussing && (
              <div className={`flex items-center gap-1 text-sm ${alwaysOpen ? 'text-secondary' : 'text-muted-foreground'}`}>
                <Users className="h-3 w-3" />
                <span className="text-xs animate-pulse">Team analyzing...</span>
              </div>
            )}
          </CardTitle>
          {!alwaysOpen && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </CardHeader>
        
        <CardContent className={`flex-1 flex flex-col p-0 ${alwaysOpen ? 'bg-background/40' : ''} overflow-hidden`}>
          <div className={`flex-1 ${alwaysOpen ? 'max-h-[500px]' : 'min-h-0'} overflow-hidden`}>
            <ScrollArea className={`${alwaysOpen ? 'h-[500px]' : 'h-full'} p-4`}>
              <div className="space-y-4">
                {messages.map((message) => (
                  <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`group relative max-w-[85%] ${
                      message.type === 'user' 
                        ? alwaysOpen ? 'bg-gradient-to-r from-primary to-primary/80 text-primary-foreground shadow-md' : 'bg-primary text-primary-foreground' 
                        : message.type === 'ai-team' 
                        ? alwaysOpen ? 'bg-gradient-to-r from-secondary/20 to-accent/20 border border-secondary/30 text-foreground' : 'bg-secondary text-secondary-foreground'
                        : alwaysOpen ? 'bg-background/80 border border-border text-foreground backdrop-blur-sm' : 'bg-muted text-muted-foreground'
                    } p-3 rounded-lg`}>
                      {message.type === 'ai-team' && (
                        <div className={`flex items-center gap-2 mb-2 text-xs ${alwaysOpen ? 'text-foreground' : 'text-foreground'}`}>
                          <span className="text-sm">
                            {AI_TEAM_MEMBERS.find(member => member.name === message.sender)?.icon}
                          </span>
                          <span className="font-medium text-black dark:text-white">{message.sender}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs text-black dark:text-white ${alwaysOpen ? 'bg-secondary/20' : 'bg-muted'}`}>
                            {AI_TEAM_MEMBERS.find(member => member.name === message.sender)?.role}
                          </span>
                        </div>
                      )}
                      
                      {(message.type === 'assistant' || message.type === 'ai-team') && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleCopyMessage(message.id, message.content)}
                          className={`absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity ${alwaysOpen ? 'text-primary hover:text-primary/80 hover:bg-primary/10' : ''}`}
                        >
                          {copiedMessageId === message.id ? (
                            <CopyCheck className="h-3 w-3" />
                          ) : (
                            <Copy className="h-3 w-3" />
                          )}
                        </Button>
                      )}
                      
                      <div className={`whitespace-pre-wrap text-sm ${(message.type === 'assistant' || message.type === 'ai-team') ? 'pr-8' : ''}`}>
                        {message.content}
                      </div>
                      
                      {message.suggestion && (
                        <div className={`mt-3 pt-3 border-t ${alwaysOpen ? 'border-primary/20' : 'border-border'}`}>
                          <Button
                            onClick={() => {
                              console.log('🤖 Apply button clicked! Suggestion:', message.suggestion);
                              handleApplySuggestion(message.suggestion);
                            }}
                            size="sm"
                            className={`w-full ${alwaysOpen ? 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground shadow-md' : ''}`}
                            variant={alwaysOpen ? "default" : "outline"}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Apply This Suggestion
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {(isLoading || isTeamDiscussing) && (
                  <div className="flex justify-start">
                    <div className={`${alwaysOpen ? 'bg-accent/50 border border-accent text-accent-foreground' : 'bg-muted text-muted-foreground'} p-3 rounded-lg flex items-center gap-2 max-w-[85%]`}>
                      <Loader2 className={`h-4 w-4 animate-spin ${alwaysOpen ? 'text-primary' : ''}`} />
                      <span className="text-sm">
                        {isTeamDiscussing ? 'AI team is analyzing your request...' : 'Generating optimal recommendation...'}
                      </span>
                    </div>
                  </div>
                )}
                {error && retryCount < 3 && (
                  <div className="flex justify-start">
                    <div className={`${alwaysOpen ? 'bg-destructive/10 border border-destructive/30 text-destructive' : 'bg-destructive/10 text-destructive'} p-3 rounded-lg max-w-[85%]`}>
                      <div className="flex items-start gap-2 mb-2">
                        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <span className="text-sm break-words">Service temporarily unavailable</span>
                      </div>
                      <Button
                        onClick={handleRetry}
                        size="sm"
                        variant="outline"
                        className={`w-full ${alwaysOpen ? 'border-destructive/30 text-destructive hover:bg-destructive/10' : ''}`}
                        disabled={isLoading}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Try Again
                      </Button>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>
          </div>

          <div className={`border-t p-4 ${alwaysOpen ? 'bg-background/50 border-primary/10 backdrop-blur-sm' : 'bg-card'} flex-shrink-0`}>
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={alwaysOpen ? "Describe your campaign objectives and goals..." : "Describe your campaign goals..."}
                disabled={isLoading || isTeamDiscussing}
                className={`flex-1 text-sm ${alwaysOpen ? 'bg-background/60 border-primary/20 focus:border-primary/40 backdrop-blur-sm' : 'bg-background'}`}
              />
              <Button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading || isTeamDiscussing}
                size="icon"
                className={`flex-shrink-0 ${alwaysOpen ? 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70' : ''}`}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
